import React, { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Typography, TextField, MenuItem, Button, useTheme, useMediaQuery, Snackbar, Alert } from "@mui/material";
import { Images } from "../../../utils/Images";
import { startLifeCampaign } from "../../../api/services/lifeCampaignService";
import {
  sanitizeIndianMobileInput,
  validateIndianMobile,
} from "../../../utils/phoneValidation";

const MEDICAL_FUNDRAISING_CMS_SECTION_ID = 96;

const MedicalFundraising = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg")); // 600–1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // 1200px+ side-by-side

  const home = useSelector((state) => state.cms.home);
  const cms = useMemo(() => {
    const sections = Array.isArray(home?.data) ? home.data : [];
    const section = sections.find((s) => s?.id === MEDICAL_FUNDRAISING_CMS_SECTION_ID);
    if (!section || section.status === 0) return null;
    return {
      section_title: section.section_title ?? "",
      title: section.title ?? "",
      sub_title: section.sub_title ?? "",
      description: section.description ?? "",
      image: section.image ?? null,
    };
  }, [home]);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [fundsUsedFor, setFundsUsedFor] = useState("");
  const [hospitalizationStatus, setHospitalizationStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const handleSnackbarClose = () => setSnackbar((s) => ({ ...s, open: false }));

  const validate = () => {
    const trimmedName = name?.trim();
    const trimmedEmail = email?.trim();
    if (!trimmedName) return { ok: false, error: "Please enter your name." };
    if (!trimmedEmail) return { ok: false, error: "Please enter your email." };
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) return { ok: false, error: "Please enter a valid email address." };
    const phoneCheck = validateIndianMobile(phone);
    if (!phoneCheck.isValid) return { ok: false, error: phoneCheck.error };
    if (!fundsUsedFor) return { ok: false, error: "Please select what the funds will be used for." };
    if (!hospitalizationStatus) return { ok: false, error: "Please select hospitalization status." };
    return { ok: true };
  };

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setFundsUsedFor("");
    setHospitalizationStatus("");
  };

  const handleSubmit = async () => {
    const validation = validate();
    if (!validation.ok) {
      setSnackbar({ open: true, message: validation.error, severity: "error" });
      return;
    }
    setLoading(true);
    try {
      await startLifeCampaign({
        name: name.trim(),
        email: email.trim(),
        mobile: validateIndianMobile(phone).digits,
        funds: fundsUsedFor,
        hospitalizationStatus,
      });
      setSnackbar({ open: true, message: "Your request has been submitted successfully. We'll get back to you soon.", severity: "success" });
      resetForm();
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Something went wrong. Please try again.";
      setSnackbar({ open: true, message, severity: "error" });
    } finally {
      setLoading(false);
    }
  };

  const fundsOptions = [
    "Medical Treatment",
    "Emergency Surgery",
    "ICU / Critical Care",
    "Accident / Trauma Care",
    "Cancer Treatment",
    "Other"
  ];

  const hospitalizationOptions = [
    "Admitted",
    "Not Admitted",
    "Discharged"
  ];

  return (
    <Box
      sx={{
        width: isMobile ? "100%" : "94.44%",
        maxWidth: "1440px",
        margin: "0 auto",
        marginTop: isMobile ? "20px" : "40px",
        marginBottom: isMobile ? "30px" : "60px",
        paddingLeft: isMobile ? "16px" : 0,
        paddingRight: isMobile ? "16px" : 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          justifyContent: "space-between",
          gap: isMobile ? "12px" : isTablet ? "28px" : "30px",
          alignItems: "stretch",
        }}
      >
        {/* Left Section - full width when stacked, flex when side-by-side */}
        <Box
          sx={{
            flex: isDesktop ? 1 : "none",
            minWidth: isDesktop ? "340px" : 0,
            width: isDesktop ? "auto" : "100%",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px", mb: isMobile ? "10px" : "14px" }}>
            <Box
              sx={{
                width: isMobile ? "6px" : "8px",
                height: isMobile ? "6px" : "8px",
                borderRadius: "50%",
                backgroundColor: "#A9C74E",
              }}
            />
            <Typography
              sx={{
                fontFamily: "Arial",
                fontWeight: 400,
                fontSize: isMobile ? "10px" : "14px",
                letterSpacing: "2px",
                textTransform: "uppercase",
                color: "#131313",
              }}
            >
              MEDICAL FUNDRAISING
            </Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "22px" : isTablet ? "28px" : "38px",
              lineHeight: isMobile ? "30px" : isTablet ? "36px" : "58px",
              color: "#1D1D1B",
              mb: isMobile ? "12px" : "10px",
            }}
          >
            {cms?.section_title ?? ""}
          </Typography>

          <Typography
            sx={{
              fontWeight: 400,
              fontSize: isMobile ? "12px" : isTablet ? "14px" : "18px",
              lineHeight: isMobile ? "20px" : isTablet ? "22px" : "28px",
              color: "#7D7D7D",
              maxWidth: isDesktop ? "700px" : "100%",
              mb: isMobile ? "8px" : "24px",
            }}
          >
            {cms?.title ?? ""}
          </Typography>

          {/* Image Card - only show when side-by-side (desktop), hide when stacked */}
          {isDesktop && cms?.image ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: "674px",
                borderRadius: "16px",
                overflow: "hidden",
                position: "relative",
                height: "400px",
                boxShadow: "0px 10px 26px 0px #0000000A",
              }}
            >
              <Box
                component="img"
                src={cms.image}
                alt=""
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  display: "block",
                }}
              />

              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  right: 0,
                  background: "#00000080",
                  backdropFilter: "blur(50px)",
                  WebkitBackdropFilter: "blur(50px)",
                  padding: "20px 20px 24px 20px",
                  borderRadius: "0 0 16px 16px",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "22px",
                    lineHeight: "32.36px",
                    color: "#FFFFFF",
                    mb: "16px",
                  }}
                >
                  {cms?.sub_title ?? ""}
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: "14px",
                    lineHeight: "24px",
                    letterSpacing: "0%",
                    textAlign: "justify",
                    color: "#E8E8E8",
                  }}
                >
                  {cms?.description ?? ""}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Box>

        {/* Right Section - Form Card */}
        <Box
          sx={{
            flex: isDesktop ? 1 : "none",
            width: isDesktop ? "auto" : "100%",
            minWidth: isDesktop ? "400px" : 0,
            maxWidth: isDesktop ? "735px" : "100%",
            minHeight: isDesktop ? "663px" : "auto",
            height: isDesktop ? "663px" : "auto",
            bgcolor: "#fff",
            borderRadius: isMobile ? "16px" : "24px",
            paddingX: isMobile ? "16px" : "30px",
            paddingY: isMobile ? "18px" : "40px",
            boxShadow: isMobile ? "0px 4px 24px rgba(0, 0, 0, 0.12)" : "0px 10px 26px 0px #0000000A",
            position: "relative",
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: isMobile ? "14px" : "22px",
              color: "#1D1D1B",
              display: "flex",
              alignItems: "center",
              gap: "10px",
              mb: "12px",
            }}
          >
            <img
              src={Images.streamlineSharpStar}
              alt="star"
              style={{ width: 22, height: 22 }}
            />
            Need Money Urgently
          </Typography>

          <Box sx={{ borderBottom: "1px solid #EFEFEF", mb: isMobile ? "14px" : "26px" }} />

          <Box sx={{ mb: "16px" }}>
            <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#141414" }}>Name</label>
            <TextField
              fullWidth
              placeholder="Enter Name"
              variant="outlined"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={loading}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiOutlinedInput-input": { padding: "12px 14px" },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "16px",
              flexDirection: { xs: "column", sm: "row" },
              mb: "21px",
            }}
          >
            <Box sx={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#141414" }}>Email ID</label>
              <TextField
                fullWidth
                placeholder="Enter Email ID"
                variant="outlined"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                }}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#141414" }}>Phone Number</label>
              <TextField
                fullWidth
                placeholder="Enter Phone Number"
                variant="outlined"
                value={phone}
                onChange={(e) => setPhone(sanitizeIndianMobileInput(e.target.value))}
                disabled={loading}
                type="tel"
                inputProps={{ inputMode: "numeric", maxLength: 10 }}
                sx={{
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: "24px" }}>
            <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#141414" }}>
              What will the funds be used for?
            </label>
            <TextField
              select
              fullWidth
              value={fundsUsedFor}
              onChange={(e) => setFundsUsedFor(e.target.value)}
              disabled={loading}
              SelectProps={{ displayEmpty: true }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                "& .MuiSelect-select": { padding: "12px 14px" },
              }}
            >
              <MenuItem value="" disabled>
                <span style={{ color: "#BDBDBD" }}>Select a option</span>
              </MenuItem>
              {fundsOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ mb: "24px" }}>
            <label style={{ display: "block", marginBottom: 10, fontWeight: 600, color: "#141414" }}>
              Hospitalization Status
            </label>
            <TextField
              select
              fullWidth
              value={hospitalizationStatus}
              onChange={(e) => setHospitalizationStatus(e.target.value)}
              disabled={loading}
              SelectProps={{ displayEmpty: true }}
              sx={{
                "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                "& .MuiSelect-select": { padding: "12px 14px" },
              }}
            >
              <MenuItem value="" disabled>
                <span style={{ color: "#BDBDBD" }}>Select Status</span>
              </MenuItem>
              {hospitalizationOptions.map((opt) => (
                <MenuItem key={opt} value={opt}>{opt}</MenuItem>
              ))}
            </TextField>
          </Box>

          <Typography
            sx={{
              fontWeight: 400,
              fontSize: isMobile ? "12px" : "14px",
              color: "#F49C2E",
              mb: "24px",
            }}
          >
            260 People started a fundraiser in last 2 days
          </Typography>

          <Button
            variant="contained"
            disableRipple
            disabled={loading}
            sx={{
              backgroundColor: "#F04951",
              borderRadius: "10px",
              boxShadow: "0px 6px 0px #141414",
              height: isMobile ? "40px" : "46px",
              width: isMobile ? "100%" : "200px",
              textTransform: "none",
              fontWeight: 700,
              "&:hover": { backgroundColor: "#e03e46" },
            }}
            onClick={handleSubmit}
          >
            {loading ? "Submitting…" : "Start a Fundraiser"}
          </Button>

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                bottom: 46,
                right: 0,
                zIndex: 10,
              }}
            >
              <img
                src={Images.stringHand}
                alt="string hand"
                style={{ width: 90, height: "auto" }}
              />
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MedicalFundraising;


